import { useJalaliLocaleListener } from "antd-jalali";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Routes as Switch, Route, Navigate } from "react-router-dom";
import { Spin } from "antd";
import { getOrganization } from "./services/auth";
import { actions as AuthActions } from "./store/authRedux/actions";
import { actions as CommonActions } from "./store/commonRedux/actions";
import { hasPermission } from "./utils/helpers";
import { useModules } from "./hooks";
import { tagClarityVisitor } from "./utils/clarity";
import { store } from "./store/store";
import { PageTransition } from "./components";

const Roles = lazy(() => import("./pages/Roles"));
const Teams = lazy(() => import("./pages/Teams"));
const Users = lazy(() => import("./pages/Users"));

const ProjectTemplates = lazy(() => import("./pages/ProjectTemplates"));
const Projects = lazy(() => import("./pages/Projects"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Organization = lazy(() => import("./pages/Organization"));
const Profile = lazy(() => import("./pages/Profile"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));
const Notifications = lazy(() => import("./pages/Notifications"));
const TimeTrackings = lazy(() => import("./pages/TimeTracking"));
const Calendar = lazy(() => import("./pages/Calendar"));
const MyTasks = lazy(() => import("./pages/MyTasks"));
const AllTasks = lazy(() => import("./pages/AllTasks"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Reports = lazy(() => import("./pages/Reports"));
const Tags = lazy(() => import("./pages/Tags"));
const Chat = lazy(() => import("./pages/Chat"));
const Tickets = lazy(() => import("./pages/Tickets"));
const Customers = lazy(() => import("./pages/Customers"));
const Letters = lazy(() => import("./pages/Letters"));
const Attendance = lazy(() => import("./pages/Attendance"));
const MyAttendance = lazy(() => import("./pages/MyAttendance"));

const PageLoader = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flex: 1,
      width: "100%",
      minHeight: 200,
    }}
  >
    <Spin size="default" />
  </div>
);

const wrap = (Page) => (
  <PageTransition>
    <Page />
  </PageTransition>
);

const BasePage = () => {
  useJalaliLocaleListener();

  const { isAuthorized } = useSelector(
    ({ auth }) => ({ isAuthorized: auth.isAuthenticated }),
    shallowEqual,
  );
  const userId = useSelector(({ auth }) => auth.user?.id);
  const ownerId = useSelector(({ auth }) => auth.organization?.owner_id);
  const canManageSubscription =
    (userId != null && String(userId) === String(ownerId)) ||
    hasPermission("manage_org");

  const dispatch = useDispatch();

  /* Subscribed rather than read straight off the store: turning a module off in
   * فضای کاری › ماژول‌ها has to take its route away in the same render, not on
   * whatever navigation happens next. */
  const { has: hasModule } = useModules();

  const [orgLoaded, setOrgLoaded] = useState(false);

  useEffect(() => {
    if (isAuthorized) {
      getOrganization()
        .then((res) => {
          const { organizations, roles, ...user } = res.user;

          dispatch(AuthActions.setUser(user));
          dispatch(AuthActions.setRoles(roles));
          res.organization &&
            dispatch(AuthActions.setOrganization(res.organization));
          organizations &&
            dispatch(AuthActions.setAllOrganizations(organizations));

          const visitor = {
            user,
            organization: res.organization,
            roles,
            theme: store.getState().common.theme,
          };
          tagClarityVisitor(visitor);
        })
        .catch(() => {})
        .finally(() => setOrgLoaded(true));
    } else {
      setOrgLoaded(true);
    }
  }, [isAuthorized]);

  /* What tells the workspace-switch splash it may come down, and the only
   * place that knows: this is the first moment the new organization's user,
   * roles and permissions are in the store, so the first moment the panel
   * underneath is actually the workspace the user asked for. The splash adds
   * its own minimum on top. See `components/OrgSwitchSplash`. */
  useEffect(() => {
    if (!orgLoaded) return;
    dispatch(CommonActions.setVariables({ orgBootDone: true }));
  }, [orgLoaded, dispatch]);

  if (isAuthorized && !orgLoaded) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {hasPermission("manage_users") && (
          <>
            <Route path="/users/*" element={wrap(Users)} />
            {}
            {}
            <Route path="/roles/*" element={wrap(Roles)} />
            <Route path="/teams/*" element={wrap(Teams)} />
          </>
        )}
        {hasPermission("manage_templates") && (
          <Route path="/projectTemplates/*" element={wrap(ProjectTemplates)} />
        )}
        <Route path="/projects/*" element={wrap(Projects)} />
        {(hasPermission("manage_projects") || hasPermission("manage_org") || hasPermission("manage_attendance")) && (
          <Route path="/reports/*" element={wrap(Reports)} />
        )}
        {hasPermission("manage_org") && (
          <Route path="/organization" element={wrap(Organization)} />
        )}
        {hasPermission("manage_org") && (
          <Route path="/tags/*" element={wrap(Tags)} />
        )}
        {hasModule("time_tracking") && (
          <Route path="/time-tracking" element={wrap(TimeTrackings)} />
        )}
        <Route path="/profile" element={wrap(Profile)} />
        {canManageSubscription && (
          <Route path="/subscriptions" element={wrap(Subscriptions)} />
        )}
        <Route path="/payment-callback" element={wrap(PaymentCallback)} />
        <Route path="/notifications" element={wrap(Notifications)} />
        <Route path="/calendar" element={wrap(Calendar)} />
        <Route path="/my-tasks" element={wrap(MyTasks)} />
        <Route path="/all-tasks" element={wrap(AllTasks)} />
        <Route path="/changelog" element={wrap(Changelog)} />
        {/* Optional modules. Turned off, the route is simply absent, so the
            catch-all below sends anyone with an old bookmark to the dashboard
            instead of an empty screen. */}
        {hasModule("customers") && hasPermission("manage_customers") && (
          <Route path="/customers/*" element={wrap(Customers)} />
        )}
        {hasModule("chat") && <Route path="/chat" element={wrap(Chat)} />}
        {hasModule("letters") && (
          <Route path="/letters/*" element={wrap(Letters)} />
        )}
        {hasModule("attendance") && hasPermission("manage_attendance") && (
          <Route path="/attendance/*" element={wrap(Attendance)} />
        )}
        {hasModule("attendance") && (
          <Route path="/my-attendance" element={wrap(MyAttendance)} />
        )}
        <Route path="/tickets/*" element={wrap(Tickets)} />
        <Route path="/" element={wrap(Dashboard)} />
        {/* The dashboard's numbers view. A route of its own rather than a query
            parameter: it is a place, it is linked to from the sidebar, and it
            is only for whoever can see organization-wide figures. */}
        {hasPermission("manage_org") && (
          <Route path="/stats" element={wrap(Dashboard)} />
        )}
        <Route path="*" element={<Navigate to={"/"} replace />} />
      </Switch>
    </Suspense>
  );
};

export default BasePage;
